import { OrgLogoMark } from "@/features/identity-hub/components/dashboard/OrgLogoMark";

interface TenantMarkProps {
  /** Null for platform-level work, or for a tenant whose row is gone. */
  name: string | null;
  logoUrl?: string | null;
  /** The tenant's own accent; `OrgLogoMark` derives one from the name if null. */
  brandColor?: string | null;
  size?: number;
}

/**
 * Dense admin rows, so the mark sits a little under the line height. Exported
 * because a row that stands a glyph in place of a logo has to reserve the same
 * width, and the two drifting apart is exactly what breaks the column.
 */
export const TENANT_MARK_SIZE = 20;

/**
 * A tenant's logo, or the space one would take.
 *
 * The blank case is deliberate rather than absent. These lists mix named
 * tenants with platform work and with tenants whose row is gone, and a row that
 * skips the mark entirely drags its name out of the column the eye is scanning
 * down. Nameless rows get no initials either: the only string left to derive
 * them from is a cuid, and "TE" for `tenant_x7k…` is worse than nothing.
 *
 * Hidden from assistive tech throughout, because every caller renders the
 * tenant's name right beside it — announcing the mark too would read the same
 * org twice, once as initials.
 */
export function TenantMark({
  name,
  logoUrl,
  brandColor,
  size = TENANT_MARK_SIZE,
}: TenantMarkProps) {
  if (name == null) {
    return (
      <span
        aria-hidden
        className="shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span aria-hidden className="flex shrink-0">
      <OrgLogoMark
        name={name}
        logoUrl={logoUrl}
        accent={brandColor}
        size={size}
      />
    </span>
  );
}
